<template>
  <v-card flat height="86vh" width-full>
    <div v-if="!ready" clas="text-center width-full">...loading...</div>

    <div v-if="ready" class="d-flex flex-column mb-6" style="height:80vh">
      <!-- message list -->
      <div
        style="flex: 2; overflow: scroll;"
        ref="chatMessages"
        v-scroll="checkIfScrolledToBottom"
      >
        <v-card v-if="channel" flat width="100%" class="overflow-y-auto frame">
          <v-card-text>
            <div v-if="!messages.length" class="text-center">
              {{ $t('enterFirstMessage') }}
            </div>
            <div v-if="messages.length && !noOlderMessages" class="text-center">
              <v-btn plain text @click="loadOlderMessages" class="text-caption">
                {{ $t('messagingViewOlderMessages') }}
              </v-btn>
            </div>
            <div
              v-if="messages.length && noOlderMessages"
              class="text-center text-caption"
            >
              {{ $t('messagingNoOlderMessages') }}
            </div>
            <message-card
              v-for="message in messages"
              :key="message.id"
              :message="message"
            />
          </v-card-text>
          <span ref="messagebottom"></span>
        </v-card>
      </div>

      <!-- message form-->
      <v-card flat width="100%" color="grey lighten-2" class="mb-4">
        <v-card-text>
          <v-textarea
            elevation="0"
            v-model="message"
            background-color="white"
            hide-details
            :label="$t('enterMessage')"
            rows="3"
            filled
            auto-grow
            @keydown.enter.exact.prevent="sendMessage"
            @keydown.enter.shift.exact.prevent="message += '\n'"
          ></v-textarea>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            large
            @click="sendMessage"
            :disabled="!message"
          >
            {{ $t('send') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </div>
  </v-card>
</template>

<script>
import { mapActions } from 'vuex'
import profileMixin from '@/components/mixins/profile'
import { channelApiBuilder, messageApiBuilder } from '../../api'
import MessageCard from '../message/card.vue'

export default {
  mixins: [profileMixin],
  components: {
    MessageCard
  },
  props: {
    channelId: {
      type: String,
      required: true
    },
    toolbar: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      channelApi: null,
      messageApi: null,
      channel: null,
      message: '',
      messages: [],
      messageOptions: {},
      noOlderMessages: false,
      isScrolledToBottom: true
    }
  },
  computed: {
    ready() {
      return this.channelApi && this.channel
    }
  },
  watch: {
    channelId(val) {
      this.fetchChannel(val)
    },
    tenantId(val) {
      if (val) {
        this.ensureApi()
      }
    },
    ready(val) {
      if (val) {
        this.scrollToBottom()
        this.markChannelAsRead()
      }
    }
  },
  async mounted() {
    await this.fetchChannel(this.channelId)

    this.$messageHub.connection.on('MessageCreated', this.handleMessageCreated)

    this.scrollToBottom()
  },
  async beforeDestroy() {
    this.$messageHub.connection.off('MessageCreated', this.handleMessageCreated)
  },
  methods: {
    ...mapActions('messaging', ['markAsRead', 'markMessageAsRead']),
    async ensureApi() {
      if (!this.channelApi) {
        this.channelApi = channelApiBuilder.build(this.tenantId)
      }
      return this.channelApi
    },
    async handleMessageCreated(channelId, message) {
      if (channelId !== this.channelId) {
        return
      }
      this.messages.push(message)
      this.markMessageAsRead({ tenantId: this.tenantId, messageId: message.id })
      this.scrollToBottom()
    },
    async fetchChannel(val) {
      await this.ensureApi()

      if (val) {
        this.channel = await this.channelApi.fetch(val)
        this.messageApi = messageApiBuilder.build(
          this.tenantId,
          this.channel.id
        )
        const messagesResponse = await this.messageApi.get(null, {
          params: this.messageOptions
        })
        this.messages = (messagesResponse.data?.results || []).sort(
          (a, b) => new Date(a.createdDateTime) - new Date(b.createdDateTime)
        )
      }
    },
    async loadOlderMessages() {
      //todo: using pagination
      this.messageOptions.beforeDateTime = this.messages[0].createdDateTime
      const messagesResponse = await this.messageApi.get(null, {
        params: this.messageOptions
      })
      const messages = (messagesResponse.data?.results || []).sort(
        (a, b) => new Date(a.createdDateTime) - new Date(b.createdDateTime)
      )

      if (!messages.length) {
        this.noOlderMessages = true
        return
      }

      this.messages = [...messages, ...this.messages]
    },
    async markChannelAsRead() {
      const payload = { tenantId: this.tenantId, channelIds: [this.channelId] }
      this.markAsRead(payload)
    },
    async sendMessage() {
      if (!this.message) {
        return
      }

      const payload = {
        tenantId: this.tenantId,
        channelId: this.channel.id,
        message: { content: this.message }
      }

      await this.$store.dispatch('messaging/sendMessage', payload)
      this.message = ''
    },
    checkIfScrolledToBottom() {
      const element = this.$refs.chatMessages
      if (element) {
        const atBottom =
          element.scrollTop === element.scrollHeight - element.offsetHeight
        this.isScrolledToBottom = atBottom
      }
      this.isScrolledToBottom = true
    },
    scrollToBottom() {
      this.$nextTick(() => {
        if (!this.ready) return

        if (this.isScrolledToBottom) {
          const element = this.$refs.chatMessages
          if (element) {
            element.scrollTop = element?.scrollHeight || 0
          }
        }
      })
    }
  }
}
</script>

<style>
.frame {
  overflow-y: auto;
}

.frame::-webkit-scrollbar {
  -webkit-appearance: none;
}

.frame::-webkit-scrollbar:vertical {
  width: 11px;
}

.frame::-webkit-scrollbar:horizontal {
  height: 11px;
}

.frame::-webkit-scrollbar-thumb {
  border-radius: 8px;
  border: 2px solid white; /* should match background, can't be transparent */
  background-color: rgba(0, 0, 0, 0.5);
}
</style>
